import React, { type ReactElement, useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { keyframes } from '@emotion/react'
import CallEndIcon from '@mui/icons-material/CallEnd'
import InfoIcon from '@mui/icons-material/Info'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded'
import QuizIcon from '@mui/icons-material/Quiz'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import SendToMobileIcon from '@mui/icons-material/SendToMobile'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import TranslateIcon from '@mui/icons-material/Translate'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Box, Collapse, Divider, IconButton, Paper, Typography } from '@mui/material'
import { type CustomPaletteColor, alpha, styled } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'

import { ChevronDownIcon, DocumentIcon, LinkIcon, ProcessIcon, TelephoneIcon } from '~assets/icons'
import { images } from '~assets/images'
import { Button } from '~components/common'
import { Chip, DocumentCard, Icon, Image } from '~components/internal'
import { lightTheme } from '~createTheme'
import { useResponseFeedback } from '~hooks/useResponseFeedback'
import { type StreamResponseStep } from '~hooks/useStreamAIResponse'
import AssistantChartRenderer from '~pages/AIAssistantScreen/components/AssistantChartRenderer'
import AssistantTableRenderer from '~pages/AIAssistantScreen/components/AssistantTableRenderer'
import { type RootState } from '~redux/rootReducer'
import { lightTokens } from '~tokens/internal'
import {
  AgentActionTypeEnum,
  type EnrichedOrganizationAISettings,
  type IAIInteractionMessageDisplay,
  type SourceDisplayLink,
  SourceType,
} from '~types'
import { extractChart } from '~utils/chartUtils'
import { sanitizeAndProcessLinks, stripHtml } from '~utils/htmlUtils'
import { getBasePath } from '~utils/navigationUtils'

import { ClearNegativeFeedbackPopover } from './ClearNegativeFeedbackPopover'
import './MessageBubble.scss'
import { ResponseFeedbackCard } from './ResponseFeedbackCard'
import { ResponseNegativeFeedbackPopover } from './ResponseNegativeFeedbackPopover'
import { PaginatedSearchResults } from './SearchView'

const wrapRedactedText = (html: string): string =>
  html.replace(
    /\[REDACTED\]/g,
    '<span class="redacted-content" aria-label="Redacted content">[REDACTED]</span>',
  )

const parseEmailContent = (
  content: string,
): { from: string; to: string; subject: string; body: string } => {
  const fromMatch = content.match(/From: ([^\n]+)/)
  const toMatch = content.match(/To: ([^\n]+)/)
  const subjectMatch = content.match(/Email Subject: ([^\n]+)/)
  const bodyMatch = content.match(/Email Body: (.+)$/s)

  return {
    from: fromMatch?.[1] || '',
    to: toMatch?.[1] || '',
    subject: subjectMatch?.[1] || '',
    body: bodyMatch?.[1] || '',
  }
}

interface MessageBubbleProps {
  message: IAIInteractionMessageDisplay
  orgAiSettings: EnrichedOrganizationAISettings
  showDebugInfo: boolean
  isNextConsecutive?: boolean
  isLog?: boolean
  test?: boolean
  disableAnimations?: boolean
  streamStep?: StreamResponseStep
  renderCharts?: boolean
  isAssistantLog?: boolean
  onFeedbackChange?: (update: {
    impression?: boolean | null
    feedback?: string
    submitter?: string
  }) => void
}

const focusRingSx = {
  '&:focus-visible': {
    outline: '2px solid rgb(8, 148, 207)',
    outlineOffset: '2px',
  },
}

const StyledLink = styled('a')({
  textDecoration: 'none',
  color: 'inherit',
  fontWeight: 'bold',
  '&:hover': {
    textDecoration: 'none',
    opacity: 0.8,
  },
  ...focusRingSx,
})

const AttachmentImagePreview: React.FC<{
  previewUrl: string
  filename: string
  downloadUrl?: string
  onError: () => void
}> = ({ previewUrl, filename, downloadUrl, onError }) => {
  const image = (
    <Image
      src={previewUrl}
      alt={filename}
      className='message-bubble__attachment-image'
      onError={onError}
    />
  )
  return downloadUrl ? (
    <a href={downloadUrl} download={filename} target='_blank' rel='noopener noreferrer'>
      {image}
    </a>
  ) : (
    image
  )
}

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
`

const initialRenderAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

export const SourceLink = ({
  orgAiSettings,
  source,
  linkToInternalPageSource,
}: {
  orgAiSettings: EnrichedOrganizationAISettings | null
  source: SourceDisplayLink
  linkToInternalPageSource: boolean
}): ReactElement => {
  const { organizationHandle } = useParams<{ organizationHandle: string }>()
  const darkGreyColor = lightTheme.palette.grey[800]

  const hasUrl = Boolean(
    (source?.page_source_id && linkToInternalPageSource && organizationHandle) ||
    source?.page_gui_url ||
    source?.page_url,
  )
  const sourceType = source.source_type as SourceType
  const typographyProps = {
    variant: 'caption' as const,
    noWrap: true,
    title: source.title,
    fontWeight: 'bold' as const,
    sx: {
      color: hasUrl
        ? orgAiSettings?.link_color
          ? `#${orgAiSettings.link_color}`
          : lightTheme.palette.primary.dark
        : darkGreyColor,
      textDecorationThickness: hasUrl ? '1px' : undefined,
      textUnderlineOffset: hasUrl ? '2px' : undefined,
      textDecoration: hasUrl ? 'underline' : 'none',
      fontWeight: 'bold',
      fontSize: '16px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginRight: '4px',
      fontFamily: orgAiSettings?.font_family || 'inherit',
      '&:hover': {
        textDecorationThickness: hasUrl ? '2px' : undefined,
        color: hasUrl
          ? orgAiSettings?.link_color
            ? `#${orgAiSettings.link_color}`
            : lightTheme.palette.primary.dark
          : undefined,
      },
    },
  }

  // If no URL is available, render as plain text
  if (!hasUrl) {
    return (
      <div
        style={{
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          flex: '1',
          maxWidth: '100%',
          gap: 4,
        }}
      >
        {sourceType === SourceType.SourceTransferLine && (
          <TelephoneIcon color={darkGreyColor} height={16} width={16} />
        )}
        {sourceType === SourceType.SourceFAQ && (
          <QuizIcon height={16} width={16} sx={{ color: darkGreyColor }} />
        )}
        {sourceType === SourceType.SourceGISLayer && (
          <Icon name='map' size={16} color={darkGreyColor} />
        )}
        <Typography {...typographyProps}>{source.title}</Typography>
      </div>
    )
  }

  // If URL is available, render with StyledLink
  if (source?.page_source_id && linkToInternalPageSource && organizationHandle) {
    return (
      <Box
        role='link'
        tabIndex={0}
        onClick={() =>
          window.open(
            `${window.location.origin}${getBasePath()}/organizations/${organizationHandle}/sources/${source.page_source_id}`,
            '_blank',
          )
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            window.open(
              `${window.location.origin}${getBasePath()}/organizations/${organizationHandle}/sources/${source.page_source_id}`,
              '_blank',
            )
          }
        }}
        sx={{
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          flex: '1',
          maxWidth: '100%',
          gap: 1,
          cursor: 'pointer',
          textDecoration: 'none',
          '&:hover': { opacity: 0.8 },
          ...focusRingSx,
        }}
        aria-label={`Source type: ${sourceType}`}
      >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {sourceType === SourceType.SourceWebsite && (
            <LinkIcon color={lightTheme.palette.text.primary} height={16} width={16} />
          )}
          {sourceType === SourceType.SourceDocument && (
            <DocumentIcon color={lightTheme.palette.text.primary} height={16} width={16} />
          )}
          {sourceType === SourceType.SourceGISLayer && (
            <Icon name='map' size={16} color={lightTheme.palette.text.primary} />
          )}
          {sourceType === SourceType.SourceTransferLine && (
            <TelephoneIcon color={lightTheme.palette.text.primary} height={16} width={16} />
          )}
        </Box>
        <Typography {...typographyProps}>Source: {source.title}</Typography>
        <OpenInNewIcon sx={{ color: 'text.primary', fontSize: 14, flexShrink: 0 }} />
      </Box>
    )
  }
  return (
    <StyledLink
      href={source?.page_gui_url || source?.page_url || undefined}
      target='_blank'
      rel='noopener noreferrer'
      sx={{
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        flex: '1',
        maxWidth: '100%',
        gap: 1,
      }}
    >
      <Box
        sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
        aria-label={`Source type: ${sourceType}`}
      >
        {sourceType === SourceType.SourceWebsite && (
          <LinkIcon color={lightTheme.palette.text.primary} height={16} width={16} />
        )}
        {sourceType === SourceType.SourceDocument && (
          <DocumentIcon color={lightTheme.palette.text.primary} height={16} width={16} />
        )}
        {sourceType === SourceType.SourcePoliProcess && (
          <ProcessIcon height={16} width={16} color={lightTheme.palette.text.primary} />
        )}
        {sourceType === SourceType.SourceFAQ && (
          <Icon name='quiz' size={16} color={lightTheme.palette.text.primary} />
        )}
        {sourceType === SourceType.SourceGISLayer && (
          <Icon name='map' size={16} color={lightTheme.palette.text.primary} />
        )}
        {sourceType === SourceType.SourceTransferLine && (
          <TelephoneIcon color={lightTheme.palette.text.primary} height={16} width={16} />
        )}
      </Box>
      <Typography {...typographyProps}>{source.title}</Typography>
      <OpenInNewIcon sx={{ color: 'text.primary', fontSize: 14, flexShrink: 0 }} />
    </StyledLink>
  )
}

export const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(
  (
    {
      message,
      orgAiSettings,
      showDebugInfo = false,
      isNextConsecutive = false,
      test = false,
      isLog = false,
      disableAnimations = false,
      streamStep,
      renderCharts = false,
      isAssistantLog = false,
      onFeedbackChange,
    },
    ref,
  ) => {
    const impression = message.impression
    const feedback = message.feedback
    const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
    const isAI = message.sender === 'AI'
    const isUser = message.sender === 'USER'
    const isAction = message.sender === 'ACTION'
    const actionType = isAction ? message.actionType : undefined
    const contentRef = useRef<HTMLDivElement>(null)
    const [showSources, setShowSources] = useState(false)
    const sourcesContainerRef = useRef<HTMLDivElement>(null)
    const [showRenderedEmail, setShowRenderedEmail] = useState(false)
    const [showInitialAnimation, setShowInitialAnimation] = useState(false)
    const [failedAttachmentImages, setFailedAttachmentImages] = useState<Set<string>>(new Set())
    const isEmail =
      isUser && message.text.startsWith('From:') && message.text.includes('Email Subject:')
    const {
      cleanedHtml: chartCleanedHtml,
      chart,
      table,
    } = renderCharts
      ? extractChart(message.text)
      : { cleanedHtml: message.text, chart: null, table: null }
    const superOnlyActionDisplay: string[] = [AgentActionTypeEnum.Enum.BACKUP_CALL]
    const darkGreyColor = lightTheme.palette.grey[800]
    const [announcementText, setAnnouncementText] = useState<string>('')

    const {
      onThumbUpClick,
      onThumbDownClick,
      submitPopoverProps,
      clearPopoverProps,
      submitFeedback,
      clearFeedback,
      announcement: feedbackAnnouncement,
    } = useResponseFeedback({
      responseId: message.id,
      impression,
      feedback,
      onChange: onFeedbackChange,
      enabled: test,
    })

    useEffect(() => {
      if (isAI && streamStep === 'complete_response' && message.text && !announcementText) {
        const rafId = requestAnimationFrame(() => {
          const finalText = stripHtml(message.text).trim()
          if (finalText && finalText !== announcementText) {
            setAnnouncementText(finalText)
          }
        })
        return (): void => cancelAnimationFrame(rafId)
      }
    }, [isAI, streamStep, message.text, announcementText])

    useEffect((): (() => void) | void => {
      if (isAI && message.id !== null && !isLog && !disableAnimations) {
        setShowInitialAnimation(true)
        const timer = setTimeout(() => setShowInitialAnimation(false), 2000)
        return () => clearTimeout(timer)
      }
    }, [isAI, message.id, isLog, disableAnimations])

    useEffect(() => {
      // This ensures that even if target="_blank" isn't working properly,
      // we still force links to open in new tabs via click handlers
      if (contentRef.current) {
        const links = contentRef.current.getElementsByTagName('a')
        const eventHandlers: { link: HTMLAnchorElement; handler: (e: Event) => void }[] = []

        for (let i = 0; i < links.length; i++) {
          const link = links[i]
          // Ensure target and rel attributes are set directly
          link.setAttribute('target', '_blank')
          link.setAttribute('rel', 'noopener noreferrer')

          const clickHandler = (e: Event): void => {
            // We'll still manually handle the click as a fallback
            e.preventDefault()
            const href = link.getAttribute('href')
            if (href) {
              window.open(href, '_blank', 'noopener,noreferrer')
            }
          }

          link.addEventListener('click', clickHandler)
          eventHandlers.push({ link, handler: clickHandler })
        }

        return (): void => {
          eventHandlers.forEach(({ link, handler }) => {
            link.removeEventListener('click', handler)
          })
        }
      }
      // We want this effect to run whenever content changes or is re-rendered
      // Adding contentRef.current as a dependency would cause infinite loops,
      // so we use a more comprehensive set of dependencies
    }, [message.text, message.id, isAI, isAction, showSources, isEmail, showRenderedEmail])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent): void => {
        if (
          sourcesContainerRef.current &&
          !sourcesContainerRef.current.contains(event.target as Node)
        ) {
          setShowSources(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return (): void => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    const poliApplySources =
      message.sources?.filter((source) => source.source_type === 'SourcePoliProcess') || []
    const poliApplyUniqueSources =
      Array.from(new Map(poliApplySources.map((source) => [source.page_url, source])).values()) ||
      []

    const nonPoliApplySources =
      message.sources?.filter((source) => source.source_type !== 'SourcePoliProcess') || []
    const nonPoliApplyUniqueSources = Array.from(
      new Map(
        nonPoliApplySources.map((source) => [source.page_url ?? source.source_id, source]),
      ).values(),
    )

    const OrganizationSourceIndicator = ({
      source,
    }: {
      source: SourceDisplayLink
    }): ReactElement => {
      const [bgcolor, color] = ['var(--offwhite)', 'var(--steel)']
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            className='flex-v-align'
            sx={{
              gap: 1,
              backgroundColor: bgcolor,
              width: 'fit-content',
              padding: '5px 10px',
              borderRadius: '5px',
            }}
          >
            {source.is_linked_organization && (
              <LinkIcon color={darkGreyColor} height={16} width={16} />
            )}
            <Typography
              variant={'caption'}
              sx={{
                fontWeight: 'bold',
                color: color,
                fontFamily: orgAiSettings.font_family || 'inherit',
              }}
            >
              {source.organization_name}
            </Typography>
          </Box>
        </Box>
      )
    }

    const hasNonPoliSources = isAI && nonPoliApplySources.length > 0

    const ConversationBubble = ({}): ReactElement => {
      return (
        <Paper
          ref={ref}
          role='article'
          sx={{
            position: 'relative',
            maxWidth: '85%',
            alignSelf: isAI ? 'flex-start' : 'flex-end',
            backgroundColor: isAI
              ? 'background.paper'
              : isLog && isEmail
                ? 'background.paper'
                : isLog && isAssistantLog
                  ? lightTokens.background.background3
                  : orgAiSettings.bg_color
                    ? `#${orgAiSettings.bg_color}`
                    : 'primary.dark',
            color: isAI
              ? 'text.primary'
              : isLog && isEmail
                ? 'text.primary'
                : isLog && isAssistantLog
                  ? 'text.primary'
                  : orgAiSettings.font_color
                    ? `#${orgAiSettings.font_color}`
                    : 'primary.contrastText',
            borderRadius: `${orgAiSettings.corner_radius ?? 8}px`,
            border: isLog && isEmail ? '1px solid' : 'none',
            borderColor: 'primary.main',
            p: 2,
            boxShadow: 'none',
            cursor: 'default',
          }}
        >
          <Box
            sx={{
              fontFamily: orgAiSettings.font_family || 'inherit',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              '& a': {
                color: `${orgAiSettings.link_color ? `#${orgAiSettings.link_color}` : lightTheme.palette.primary.dark} !important`,
                fontSize: '16px !important',
                fontWeight: 'bold !important',
                target: '_blank',
                rel: 'noopener noreferrer',
                fontFamily: 'inherit',
                textDecoration: 'underline',
                textDecorationThickness: '1px',
                textUnderlineOffset: '2px',
                '&:hover': {
                  textDecoration: 'underline',
                  textDecorationThickness: '2px',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                },
              },
              '& p': {
                display: 'block',
                margin: '6px 0',
                lineHeight: 1.6,
                fontSize: 16,
                fontFamily: 'inherit',
              },
              '& div': {
                fontSize: 16,
                fontFamily: 'inherit',
              },
              '& li': {
                marginBottom: 1,
                lineHeight: 1.6,
                fontSize: 16,
                fontFamily: 'inherit',
              },
              '& li p': {
                display: 'inline',
                margin: 0,
                fontSize: 16,
                fontFamily: 'inherit',
              },
              '& ul, & ol': {
                marginTop: 1.5,
                marginBottom: 1.5,
                paddingLeft: 3,
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                margin: '12px 0 6px',
                lineHeight: 1.3,
                fontFamily: 'inherit',
              },
              '& h1': { fontSize: 24 },
              '& h2': { fontSize: 20 },
              '& h3': { fontSize: 18 },
              '& blockquote': {
                borderLeft: '4px solid #e0e0e0',
                margin: '8px 0',
                padding: '0.25em 0 0.25em 0.5em',
                fontStyle: 'italic',
                color: 'text.secondary',
                fontFamily: 'inherit',
              },
              '& code': {
                backgroundColor: '#f5f5f5',
                padding: '0.125em 0.25em',
                borderRadius: '3px',
                fontSize: 14,
                fontFamily: 'monospace',
              },
              '& pre': {
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: '4px',
                overflow: 'auto',
                margin: '8px 0',
              },
              '& pre code': {
                padding: 0,
                backgroundColor: 'transparent',
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '4px',
                margin: '1.5 0',
                fontFamily: 'inherit',
              },
              '& .redacted-content': {
                backgroundColor: 'currentColor',
                borderRadius: '3px',
                cursor: 'help',
                position: 'relative',
                display: 'inline',
                padding: '0 2px',
                userSelect: 'none',
                '&::after': {
                  content: '"Redacted Content"',
                  position: 'absolute',
                  bottom: 'calc(100% + 4px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.75)',
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  opacity: 0,
                  transition: 'opacity 0.15s ease',
                  zIndex: 1000,
                },
                '&:hover::after': {
                  opacity: 1,
                },
              },
            }}
          >
            <div ref={contentRef} className='prose max-w-none'>
              {isEmail ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <IconButton
                      size='small'
                      onClick={() => setShowRenderedEmail(!showRenderedEmail)}
                      sx={{ color: 'inherit', opacity: 0.8 }}
                    >
                      {showRenderedEmail ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Box>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: showRenderedEmail
                        ? wrapRedactedText(sanitizeAndProcessLinks(message.text))
                        : wrapRedactedText(
                            sanitizeAndProcessLinks(parseEmailContent(message.text).body, {
                              formatPhoneNumbers: true,
                            }),
                          ),
                    }}
                  />
                </>
              ) : isAction ? (
                <div dangerouslySetInnerHTML={{ __html: sanitizeAndProcessLinks(message.text) }} />
              ) : (
                <>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: wrapRedactedText(sanitizeAndProcessLinks(chartCleanedHtml)),
                    }}
                  />
                  {chart && <AssistantChartRenderer chart={chart} size='small' />}
                  {table && <AssistantTableRenderer table={table} />}
                </>
              )}
              {message.location && (
                <Box sx={{ mt: 1 }}>
                  <Divider
                    sx={{
                      my: 1,
                      borderColor: orgAiSettings.font_color
                        ? `#${orgAiSettings.font_color || '000000'}`
                        : 'primary.contrastText',
                    }}
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      color: orgAiSettings.font_color
                        ? `#${orgAiSettings.font_color || '000000'}`
                        : 'primary.contrastText',
                      fontSize: '14px',
                      fontFamily: orgAiSettings.font_family || 'inherit',
                    }}
                  >
                    {message.location.lat.toFixed(4)}, {message.location.lon.toFixed(4)}
                  </Typography>
                </Box>
              )}
            </div>
          </Box>
          {hasNonPoliSources && (
            <Box
              sx={{
                mt: 2,
              }}
            >
              <Divider />
              <Button
                variant='text'
                maxWidth='none'
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  setShowSources((prevState) => !prevState)
                }}
                aria-expanded={showSources}
                aria-controls={`sources-content-${message.id}`}
                aria-label={`View Sources ${showSources ? 'hide' : 'show'}`}
                sx={{
                  mt: 1,
                  color: orgAiSettings.link_color
                    ? `#${orgAiSettings.link_color}`
                    : lightTheme.palette.primary.dark,
                }}
              >
                <Box
                  className='flex-v-align'
                  aria-hidden='true'
                  sx={{
                    mt: 0.5,
                    gap: 1,
                  }}
                >
                  <InfoIcon sx={{ fontSize: 20 }} />
                  <Typography
                    variant='caption'
                    sx={{
                      fontWeight: 'bold',
                      ml: 0.5,

                      color: orgAiSettings.link_color
                        ? `#${orgAiSettings.link_color}`
                        : lightTheme.palette.primary.dark,

                      display: 'block',
                      fontSize: '16px',
                      fontFamily: orgAiSettings.font_family || 'inherit',
                    }}
                  >
                    View Sources:
                  </Typography>
                  <Box
                    aria-hidden='true'
                    sx={{
                      ml: 1,
                      display: 'flex',
                      transform: showSources ? 'rotate(180deg)' : 'none',
                    }}
                  >
                    <ChevronDownIcon color={lightTheme.palette.text.primary} />
                  </Box>
                </Box>
              </Button>

              <Collapse in={showSources}>
                <Box id={`sources-content-${message.id}`} tabIndex={-1}>
                  {(showDebugInfo ? nonPoliApplySources : nonPoliApplyUniqueSources).map(
                    (source, index) => (
                      <Box
                        key={index}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        <Box
                          className='flex-v-align'
                          sx={{
                            display: 'flex',
                            justifyContent: 'left',
                            gap: 1,
                          }}
                        >
                          <SourceLink
                            orgAiSettings={orgAiSettings}
                            source={source}
                            linkToInternalPageSource={false}
                          />
                          {!isMobile && (
                            <>
                              <OrganizationSourceIndicator source={source} />
                              {Boolean(showDebugInfo && source.relevance_score) && (
                                <Typography
                                  variant='caption'
                                  sx={{
                                    fontFamily: orgAiSettings.font_family || 'inherit',
                                    color: 'text.secondary',
                                  }}
                                >
                                  Score: {source.relevance_score}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    ),
                  )}
                </Box>
              </Collapse>
            </Box>
          )}
        </Paper>
      )
    }

    const ActionBubble = ({}): ReactElement => {
      if (actionType === AgentActionTypeEnum.Enum.SEARCH_RESULTS) {
        return (
          <Paper
            ref={ref}
            role='article'
            aria-label='Search results'
            sx={{ width: '100%', borderRadius: 3, boxShadow: 1, py: 1 }}
          >
            {message.sources?.length ? (
              <PaginatedSearchResults
                sources={message.sources}
                pageSize={5}
                debug={showDebugInfo}
                theme={lightTheme}
                orgAiSettings={orgAiSettings}
              />
            ) : (
              <Typography sx={{ m: 3 }} color='text.secondary'>
                No results returned
              </Typography>
            )}
          </Paper>
        )
      }

      const shouldDisplayAction = isSuper || !superOnlyActionDisplay.includes(actionType || 'NONE')

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
          }}
        >
          {shouldDisplayAction && (
            <Chip
              icon={
                actionType === AgentActionTypeEnum.Enum.CALL_ENDED ? (
                  <CallEndIcon color='error' />
                ) : actionType === AgentActionTypeEnum.Enum.CALL_RECEIVED ? (
                  <PhoneCallbackIcon color='success' />
                ) : actionType === AgentActionTypeEnum.Enum.CALL_TRANSFERRED ? (
                  <PhoneForwardedIcon color='primary' />
                ) : actionType === AgentActionTypeEnum.Enum.SMS_SENT ? (
                  <SendToMobileIcon color='primary' />
                ) : actionType === AgentActionTypeEnum.Enum.LANGUAGE_CHANGE ? (
                  <TranslateIcon color='primary' />
                ) : actionType === AgentActionTypeEnum.Enum.BACKUP_CALL ? (
                  <ReplayOutlinedIcon color='warning' />
                ) : (
                  <></>
                )
              }
              label={
                actionType !== AgentActionTypeEnum.Enum.SMS_SENT
                  ? stripHtml(message.text)
                  : 'Sent SMS'
              }
              size='medium'
              sx={{
                color: 'text.primary',
                py: 1.5,
                px: 2,
                bgcolor: (theme) => {
                  let color
                  if (actionType === AgentActionTypeEnum.Enum.CALL_ENDED) {
                    color = theme.palette.error.main
                  } else if (actionType === AgentActionTypeEnum.Enum.CALL_RECEIVED) {
                    color = theme.palette.success.main
                  } else if (
                    actionType === AgentActionTypeEnum.Enum.CALL_TRANSFERRED ||
                    actionType === AgentActionTypeEnum.Enum.LANGUAGE_CHANGE ||
                    actionType === AgentActionTypeEnum.Enum.SMS_SENT
                  ) {
                    color = theme.palette.primary.main
                  } else if (actionType === AgentActionTypeEnum.Enum.BACKUP_CALL) {
                    color = theme.palette.warning.main
                  } else {
                    color = theme.palette.grey[500]
                  }
                  return alpha(color, 0.12)
                },
                fontSize: '16px',
              }}
            />
          )}
          {shouldDisplayAction && actionType === AgentActionTypeEnum.Enum.SMS_SENT && (
            <Paper
              ref={ref}
              role='article'
              aria-label={`SMS sent`}
              sx={{
                position: 'relative',
                maxWidth: '66%',
                alignSelf: 'center',
                backgroundColor: 'grey.100',
                color: 'text.primary',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.300',
                p: 2,
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                boxShadow: 1,
              }}
            >
              <div ref={contentRef} className='prose max-w-none' style={{ fontStyle: 'italic' }}>
                {stripHtml(message.text)}
              </div>
            </Paper>
          )}
        </Box>
      )
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          mb: isNextConsecutive ? '2px' : 2,
          position: 'relative',
        }}
      >
        {/* Screen reader only - announces complete message once during streaming */}
        {isAI && (
          <Box
            role='status'
            aria-live={streamStep === 'complete_response' ? 'polite' : 'off'}
            aria-atomic='true'
            sx={{ ...visuallyHidden }}
          >
            {announcementText}
          </Box>
        )}
        {isUser && message.attachments && message.attachments.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.5,
              mb: 0.5,
              maxWidth: '75%',
            }}
          >
            {message.attachments.map((att, idx) => {
              const key = `${att.filename}-${idx}`
              const showImage = att.is_image && att.preview_url && !failedAttachmentImages.has(key)
              return showImage ? (
                <AttachmentImagePreview
                  key={key}
                  previewUrl={att.preview_url ?? ''}
                  filename={att.filename}
                  downloadUrl={att.download_url}
                  onError={() => setFailedAttachmentImages((prev) => new Set(prev).add(key))}
                />
              ) : att.download_url ? (
                <a
                  key={key}
                  href={att.download_url}
                  download={att.filename}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ textDecoration: 'none' }}
                >
                  <Chip
                    clickable
                    icon={<Icon name='AttachFile' />}
                    label={att.filename}
                    sx={{ backgroundColor: 'white' }}
                  />
                </a>
              ) : (
                <Chip
                  key={key}
                  icon={<Icon name='AttachFile' />}
                  label={att.filename}
                  sx={{ backgroundColor: 'white' }}
                />
              )
            })}
          </Box>
        )}
        {isAction ? ActionBubble({}) : ConversationBubble({})}
        {isAI && message.files && message.files.length > 0 && (
          <Box className='message-bubble__files'>
            {message.files.map((file) => (
              <DocumentCard
                key={file.download_url}
                doc={{
                  key: file.download_url,
                  name: file.filename,
                  url: file.download_url,
                  category: 'generated',
                }}
                trailing='open'
              />
            ))}
          </Box>
        )}
        {poliApplyUniqueSources.length > 0 && (
          <Box sx={{ mt: 1, maxWidth: '85%' }}>
            {poliApplyUniqueSources.map((source, index) => {
              const url = source?.page_gui_url || source?.page_url

              return (
                <StyledLink
                  key={index}
                  href={url || '#'}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    border: `3px solid ${alpha(`#${orgAiSettings.bg_color}`, 0.5)}`,
                    backgroundColor: `${alpha(`#${orgAiSettings.bg_color}`, 0.1)}`,
                    borderRadius: '8px',
                    marginBottom: '12px',
                    padding: '10px 15px',
                    width: '100%',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      marginRight: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Image src={images.logo} alt='' width='24px' height='24px' />
                  </Box>
                  <Typography
                    variant='body2'
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexGrow: 1,
                      fontFamily: orgAiSettings.font_family || 'inherit',
                      color: orgAiSettings.link_color
                        ? `#${orgAiSettings.link_color}`
                        : lightTheme.palette.primary.dark,
                    }}
                  >
                    <span style={{ fontWeight: 'bold' }}>Click to Apply: </span>
                    <span style={{ fontWeight: 'bold' }}>{source.title}</span>
                  </Typography>
                </StyledLink>
              )
            })}
          </Box>
        )}
        {isAI &&
          message.id !== null &&
          (!isLog || impression !== null) &&
          (isLog ? (
            <Box sx={{ mt: 2, width: '85%', mx: 'auto' }}>
              <ResponseFeedbackCard
                impression={impression as boolean}
                feedback={message.feedback}
                submitter={message.submitter}
                created={message.feedbackCreated as Date}
              />
            </Box>
          ) : (
            // In interactive mode, render as clickable buttons
            <Box
              sx={{ mt: 1, ml: 2, display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
              role='group'
              aria-label='Was this answer helpful?'
            >
              <>
                <IconButton
                  onClick={onThumbUpClick}
                  color={impression === true ? 'success' : 'default'}
                  aria-label='Helpful'
                  aria-pressed={impression === true}
                  sx={{
                    ...focusRingSx,
                    ...(impression === true && {
                      backgroundColor: (lightTheme.palette.success as CustomPaletteColor).states
                        .selected,
                    }),
                    ...(showInitialAnimation &&
                      !disableAnimations && {
                        animation: `${initialRenderAnimation} 2s ease-out`,
                      }),
                    ...(!disableAnimations && {
                      '&:hover': {
                        animation: `${bounceAnimation} 0.6s ease-in-out`,
                      },
                    }),
                  }}
                >
                  {impression === true ? <ThumbUpAltIcon /> : <ThumbUpOffAltIcon />}
                </IconButton>
                <IconButton
                  onClick={onThumbDownClick}
                  color={impression === false ? 'warning' : 'default'}
                  aria-label='Not Helpful'
                  aria-pressed={impression === false}
                  sx={{
                    ...focusRingSx,
                    ...(impression === false && {
                      backgroundColor: (lightTheme.palette.warning as CustomPaletteColor).states
                        .selected,
                    }),
                    ...(showInitialAnimation &&
                      !disableAnimations && {
                        animation: `${initialRenderAnimation} 2s ease-out`,
                      }),
                    ...(!disableAnimations && {
                      '&:hover': {
                        animation: `${bounceAnimation} 0.6s ease-in-out`,
                      },
                    }),
                  }}
                >
                  {impression === false ? <ThumbDownAltIcon /> : <ThumbDownOffAltIcon />}
                </IconButton>
                <Box role='status' aria-live='polite' aria-atomic='true' sx={visuallyHidden}>
                  {feedbackAnnouncement}
                </Box>
                {!!feedback && <Typography variant='caption'>Feedback recorded</Typography>}
                {test && (
                  <ResponseNegativeFeedbackPopover
                    submitFeedback={submitFeedback}
                    organizationId={orgAiSettings.organization_id}
                    chatPersistenceMinutes={orgAiSettings.chat_persistence_minutes}
                    {...submitPopoverProps}
                  />
                )}
                {test && (
                  <ClearNegativeFeedbackPopover onConfirm={clearFeedback} {...clearPopoverProps} />
                )}
              </>
            </Box>
          ))}
      </Box>
    )
  },
)
